import ReactMarkdown from 'react-markdown';
import { Histogram, TimeSeries, Line as LineChart, Bar as BarChart, Pie as PieChart } from '../charts/index.js';
import MapView from '../maps/MapView';

const MessageContent = ({ content, type }) => {
  // Add error boundary
  try {
    // Handle different content types
    switch (type) {
      case 'markdown':
        return (
          <div className="prose prose-sm max-w-none prose-pre:bg-gray-100">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        );
      
      case 'chart':
        const { chartType, data, options } = content;
        
        // Map the chart type to the appropriate component
        switch (chartType?.toLowerCase()) {
          case 'histogram':
            return <Histogram data={data} options={options} />;
          case 'bar':
            return <BarChart data={data} options={options} />;
          case 'line':
            return <LineChart data={data} options={options} />;
          case 'pie':
          case 'doughnut':
            return <PieChart data={data} options={options} />;
          case 'timeseries':
          default:
            return <TimeSeries data={data} options={options} />;
        }
      
      case 'map':
        return <MapView data={content} />;
      
      case 'table':
        // Handle table rendering
        if (!content?.rows || !Array.isArray(content.rows) || content.rows.length === 0) {
          return <div className="text-gray-500 text-sm">No data available</div>;
        }
        
        // Check if rows are arrays or objects
        const rowsAreArrays = Array.isArray(content.rows[0]);
        
        // Get columns - either from content.columns or from the first row
        const columns = content.columns || 
          (rowsAreArrays ? content.columns : Object.keys(content.rows[0]));
        
        // Check if there's only one row - if so, render as key-value pairs
        if (content.rows.length === 1) {
          const row = content.rows[0];
          
          return (
            <div className="bg-white rounded">
              {columns.map((column, index) => {
                const value = rowsAreArrays ? row[index] : row[column];
                return (
                  <div key={index} className="flex px-6 py-1.5">
                    <div className="w-1/3 text-gray-600 text-sm">{column}</div>
                    <div className="w-2/3 text-gray-900 font-medium text-sm">
                      {value !== null && value !== undefined ? String(value) : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        
        // For multiple rows, render as a table (existing logic)
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {content.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {rowsAreArrays ? (
                      // Handle rows as arrays - use index to access values
                      row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {cell !== null && cell !== undefined ? String(cell) : ''}
                        </td>
                      ))
                    ) : (
                      // Handle rows as objects - use column names to access values
                      columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {row[column] !== null && row[column] !== undefined ? String(row[column]) : ''}
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      
      default:
        return <div>{JSON.stringify(content)}</div>;
    }
  } catch (error) {
    return (
      <div className="text-red-500">
        Error rendering content: {error.message}
      </div>
    );
  }
};

export default MessageContent; 